import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ClipboardCheck, FileText, Hammer, ShieldCheck } from "lucide-react";
import PageShell, { PageHeader } from "@/components/PageShell";
import { SERVICES } from "@/data/site";

const PROCESS = [
  { icon: ClipboardCheck, n: "01", title: "Helyszíni felmérés", duration: "Ingyenes",     phase: "Ingyenes",   body: "Felmegyünk a tetőre, kibontunk egy cserepet, megnézzük a szarufát — kötelezettség nélkül." },
  { icon: FileText,       n: "02", title: "Tételes ajánlat",    duration: "Írásban",      phase: "Írásban",    body: "Sorról sorra látja, mit fizet. A végszámla a tételes ajánlathoz igazodik." },
  { icon: Hammer,         n: "03", title: "Kivitelezés",        duration: "Saját brigád", phase: "Saját brigád", body: "Az állvány az ár része, naponta tiszta munkaterület. Reggel egyeztetünk, este takarítunk." },
  { icon: ShieldCheck,    n: "04", title: "Garancia",           duration: "Aláírva",      phase: "Aláírva",    body: "A garancialevelet a befejezéskor átadjuk. Aláírjuk — nem cég-pecsétes papír." },
];

const REASONS = [
  { stat: "Saját", label: "brigád", body: "Mindenki, aki a tetőn dolgozik, a saját csapatunk tagja — nincs alvállalkozó." },
  { stat: "Helyszíni", label: "felmérés", body: "Telefonon nem árazunk. Minden ajánlat a tetőről, a tényleges állapot alapján készül." },
  { stat: "Írásos", label: "garancia", body: "Minden munkára írásos garanciát adunk, amit a befejezéskor átadunk." },
];

export default function MunkakPage() {
  return (
    <PageShell>
      <PageHeader
        number="02"
        eyebrow="Munkák"
        title="Öt szakterület,"
        titleAccent="komolyan véve."
        lead="A bádogos munka a szakterületünk — szegélyek, ereszek, kéménybekötések és korcolt lemezfedés. Mellette tetőszerkezet, cseréptető és felújítás is, mindig személyes odafigyeléssel."
      />

      {/* Services list */}
      <section className="section" style={{ background: "var(--ink)" }}>
        <div className="site-container">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`page-service-${s.slug}`}
              className="srv-row"
            >
              <span className="srv-num">{s.number}</span>
              <div>
                <h3 className="srv-title">{s.title}</h3>
                <p className="body" style={{ margin: "6px 0 0", maxWidth: 720 }}>
                  {s.summary}
                </p>
              </div>
              <span className="srv-arrow"><ArrowUpRight size={20} strokeWidth={1.6} /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Process — clean minimal flow */}
      <section className="section" style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", position: "relative" }}>
        <div className="site-container" style={{ position: "relative", zIndex: 1 }}>
          <div className="proc-head" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, marginBottom: 64 }}>
            <style>{`@media (min-width: 900px) { .proc-head { grid-template-columns: 1fr 1fr !important; gap: 80px !important; align-items: end !important; } }`}</style>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
                <span className="eyebrow">Folyamat</span>
              </div>
              <h2 className="h2" style={{ margin: 0 }}>
                Hogyan dolgozunk <span style={{ color: "var(--gold)" }}>az első hívástól a garanciáig.</span>
              </h2>
            </div>
            <p className="lead" style={{ margin: 0, maxWidth: 440 }}>
              Mindegyik szakaszban tudja, hol tart a munka, mennyi van hátra, és mi a következő lépés. Nincs meglepetés a végszámlán.
            </p>
          </div>

          <div className="proc-flow" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 0, borderTop: "1px solid var(--line)" }}>
            <style>{`
              @media (min-width: 900px) {
                .proc-flow { grid-template-columns: repeat(4, 1fr) !important; border-top: none !important; }
                .proc-flow .proc-step { border-top: 1px solid var(--line) !important; border-right: 1px solid var(--line) !important; }
                .proc-flow .proc-step:last-child { border-right: none !important; }
              }
              .proc-step { transition: background 240ms ease; }
              .proc-step:hover { background: var(--ink-3); }
              .proc-step:hover .proc-num { color: var(--gold); }
              .proc-step:hover .proc-icon { color: var(--gold); }
            `}</style>

            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.n}
                  data-testid={`process-${i}`}
                  className="proc-step"
                  style={{
                    padding: "36px 28px",
                    borderBottom: "1px solid var(--line)",
                    display: "flex", flexDirection: "column", gap: 18,
                    minHeight: 260,
                  }}
                >
                  {/* meta row: step number + duration */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span className="proc-num font-display" style={{
                      fontSize: 13, color: "var(--text-dim)", letterSpacing: "0.06em",
                      transition: "color 240ms ease",
                    }}>
                      {p.n} · Lépés
                    </span>
                    <span className="small" style={{ color: "var(--gold)", letterSpacing: "0.04em" }}>
                      {p.duration}
                    </span>
                  </div>

                  {/* icon */}
                  <Icon className="proc-icon" size={26} strokeWidth={1.4} color="var(--text)" style={{ transition: "color 240ms ease" }} />

                  {/* title */}
                  <h3 className="h3" style={{ margin: 0, fontSize: 19 }}>
                    {p.title}
                  </h3>

                  {/* body */}
                  <p className="body" style={{ margin: 0, marginTop: "auto" }}>
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why us — 3 large stats */}
      <section className="section bg-grid" style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", position: "relative" }}>
        <div className="site-container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
            <span className="eyebrow">Miért engem</span>
          </div>
          <h2 className="h2" style={{ margin: 0, marginBottom: 56, maxWidth: 720 }}>
            Három dolog, amiben <span style={{ color: "var(--gold)" }}>biztos lehet.</span>
          </h2>
          <style>{`
            .reasons-grid { grid-template-columns: 1fr; }
            @media (min-width: 900px) { .reasons-grid { grid-template-columns: repeat(${REASONS.length}, 1fr); } }
          `}</style>
          <div className="reasons-grid" style={{
            display: "grid",
            gap: 1, background: "var(--line)",
          }}>
            {REASONS.map((r, i) => (
              <div key={r.label} data-testid={`reason-${i}`} style={{ background: "var(--ink-2)", padding: "40px 32px" }}>
                <div className="font-display" style={{
                  fontSize: "clamp(1.5rem, 3vw, 2.25rem)", color: "var(--gold)",
                  letterSpacing: "-0.025em", lineHeight: 1.05, marginBottom: 6,
                }}>
                  {r.stat}
                </div>
                <span className="eyebrow-muted">{r.label}</span>
                <p className="body" style={{ marginTop: 16, maxWidth: 280 }}>{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </PageShell>
  );
}
